import { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaCheckCircle } from 'react-icons/fa';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 1200);
  };

  return (
    <div className="bg-white">

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="relative max-w-7xl mx-auto px-4">
          <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Reach Out</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Contact Us</h1>
          <p className="text-blue-200 max-w-xl text-lg leading-relaxed">
            Have a project in mind or need IT staffing support? We'd love to hear from you. Our team typically responds within one business day.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" className="w-full fill-white" preserveAspectRatio="none">
            <path d="M0,40 C480,0 960,40 1440,0 L1440,40 Z" />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-[#0c1b4d] mb-6">Send Us a Message</h2>

              {submitted ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaCheckCircle className="text-emerald-500" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-800 mb-2">Message Sent!</h3>
                  <p className="text-emerald-600">
                    Thank you for reaching out. A member of our team will be in touch within one business day.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', service: '', message: '' }); }}
                    className="mt-6 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(617) 555-0100"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Service of Interest</label>
                      <select
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow text-gray-600"
                      >
                        <option value="">Select a service...</option>
                        <option>IT Consulting & Strategy</option>
                        <option>Cloud Solutions</option>
                        <option>Software Development</option>
                        <option>Managed IT Services</option>
                        <option>Data & Analytics</option>
                        <option>Cybersecurity</option>
                        <option>IT Staffing</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or how we can help..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Sending...
                      </>
                    ) : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              <div className="bg-[#0c1b4d] rounded-2xl p-7 text-white">
                <h3 className="font-bold text-lg mb-5">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FaMapMarkerAlt size={14} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Office Address</p>
                      <p className="text-blue-200 text-sm mt-0.5">Zepaj Ln<br />Lynnfield, MA 01940</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaPhone size={13} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Phone</p>
                      <p className="text-blue-200 text-sm mt-0.5">617-860-7304</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaEnvelope size={13} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Email</p>
                      <a href="mailto:hr@marvyitservices.com" className="text-blue-200 text-sm mt-0.5 hover:text-cyan-400 transition-colors">
                        hr@marvyitservices.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaClock size={13} className="text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Business Hours</p>
                      <p className="text-blue-200 text-sm mt-0.5">Mon – Fri: 9:00 AM – 6:00 PM EST</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-50 border border-cyan-100 rounded-2xl p-6">
                <h4 className="font-bold text-[#0c1b4d] mb-2">For IT Staffing Inquiries</h4>
                <p className="text-gray-600 text-sm mb-3">Looking to place or find IT talent? Our recruiters are ready to help.</p>
                <a
                  href="mailto:hr@marvyitservices.com"
                  className="inline-block text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Email our recruiters →
                </a>
              </div>

              <div className="bg-slate-50 border border-gray-100 rounded-2xl p-6">
                <h4 className="font-bold text-[#0c1b4d] mb-2">Emergency IT Support</h4>
                <p className="text-gray-600 text-sm mb-3">Managed services clients can reach our 24/7 NOC directly.</p>
                <span className="text-sm font-semibold text-[#0c1b4d]">617-860-7304 (Press 2)</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
